<script setup lang="ts">
import type { LogoSize, LogoVariant } from './use-logo'

defineOptions({ name: 'MsLogo' })

// The metastory mark, drawn rather than fetched.
withDefaults(
  defineProps<{
    /** The glyph on its own, or the glyph with the wordmark beside it. */
    variant?: LogoVariant
    /** Sets the height; the width follows from the artwork. */
    size?: LogoSize
    /** The accessible name. */
    label?: string
  }>(),
  { variant: 'mark', size: 'default', label: undefined },
)
</script>

<template>
  <span
    class="ms-logo"
    :class="[`ms-logo--${variant}`, `ms-logo--${size}`]"
    :role="label ? 'img' : undefined"
    :aria-label="label"
    :aria-hidden="label ? undefined : true"
  >
    <svg
      v-if="variant === 'mark'"
      class="ms-logo__art"
      viewBox="0 0 683 751"
      fill="none"
      aria-hidden="true"
    >
      <path fill="currentColor"
        fill-rule="evenodd"
        clip-rule="evenodd"
        d="M0 227.582C0 177.307 40.772 136.551 91.0668 136.551H409.801C434.948 136.551 455.334 156.929 455.334 182.067C455.334 207.204 434.948 227.582 409.801 227.582H91.0668V523.432H409.801C434.948 523.432 455.334 543.81 455.334 568.948C455.334 594.085 434.948 614.463 409.801 614.463H91.0668C40.772 614.463 0 573.707 0 523.432V227.582Z"
      />
      <path fill="currentColor"
        d="M500.87 91.0354H591.936V659.978H500.87V91.0354Z"
      />
      <path fill="currentColor"
        d="M409.799 45.5154C409.799 20.3779 430.185 0 455.333 0H637.466C662.614 0 683 20.3779 683 45.5154V45.5154C683 70.6529 662.614 91.0308 637.466 91.0308H455.333C430.185 91.0308 409.799 70.6529 409.799 45.5154V45.5154Z"
      />
      <path fill="currentColor"
        d="M409.799 705.485C409.799 680.347 430.185 659.969 455.333 659.969H637.466C662.614 659.969 683 680.347 683 705.485V705.485C683 730.622 662.614 751 637.466 751H455.333C430.185 751 409.799 730.622 409.799 705.485V705.485Z"
      />
    </svg>
    <svg
      v-if="variant === 'full'"
      class="ms-logo__art"
      viewBox="0 0 278 50"
      fill="none"
      aria-hidden="true"
    >
      <path fill="currentColor"
        d="M81.544 13.578C84.296 13.578 86.488 14.474 88.12 16.266C89.784 18.058 90.616 20.458 90.616 23.466V38.25H84.424V23.898C84.424 22.458 84.072 21.338 83.368 20.538C82.664 19.738 81.672 19.338 80.392 19.338C78.984 19.338 77.88 19.802 77.08 20.73C76.312 21.658 75.928 23.002 75.928 24.762V38.25H69.736V23.898C69.736 22.458 69.384 21.338 68.68 20.538C67.976 19.738 66.984 19.338 65.704 19.338C64.328 19.338 63.224 19.802 62.392 20.73C61.592 21.658 61.192 23.002 61.192 24.762V38.25H55V14.25H61.192V16.794C62.632 14.65 64.856 13.578 67.864 13.578C70.808 13.578 72.984 14.73 74.392 17.034C75.992 14.73 78.376 13.578 81.544 13.578Z"
      />
      <path fill="currentColor"
        d="M101.107 28.794C101.939 31.802 104.195 33.306 107.875 33.306C110.243 33.306 112.035 32.506 113.251 30.906L118.243 33.786C115.875 37.21 112.387 38.922 107.779 38.922C103.811 38.922 100.627 37.722 98.227 35.322C95.827 32.922 94.627 29.898 94.627 26.25C94.627 22.634 95.811 19.626 98.179 17.226C100.547 14.794 103.587 13.578 107.299 13.578C110.819 13.578 113.715 14.794 115.987 17.226C118.291 19.658 119.443 22.666 119.443 26.25C119.443 27.05 119.363 27.898 119.203 28.794H101.107ZM101.011 23.994H113.251C112.899 22.362 112.163 21.146 111.043 20.346C109.955 19.546 108.707 19.146 107.299 19.146C105.635 19.146 104.259 19.578 103.171 20.442C102.083 21.274 101.363 22.458 101.011 23.994Z"
      />
      <path fill="currentColor"
        d="M136.79 20.202H131.367V30.186C131.367 31.018 131.575 31.626 131.991 32.01C132.407 32.394 133.014 32.618 133.814 32.682C134.615 32.714 135.606 32.698 136.79 32.634V38.25C132.535 38.73 129.527 38.33 127.767 37.05C126.039 35.77 125.174 33.482 125.174 30.186V20.202H120.998V14.25H125.174V9.402L131.367 7.53V14.25H136.79V20.202Z"
      />
      <path fill="currentColor"
        d="M158.646 14.25H164.838V38.25H158.646V35.418C156.79 37.754 154.182 38.922 150.822 38.922C147.622 38.922 144.87 37.706 142.566 35.274C140.294 32.81 139.158 29.802 139.158 26.25C139.158 22.698 140.294 19.706 142.566 17.274C144.87 14.81 147.622 13.578 150.822 13.578C154.182 13.578 156.79 14.746 158.646 17.082V14.25ZM147.222 31.146C148.47 32.394 150.054 33.018 151.974 33.018C153.894 33.018 155.478 32.394 156.726 31.146C158.006 29.866 158.646 28.234 158.646 26.25C158.646 24.266 158.006 22.65 156.726 21.402C155.478 20.122 153.894 19.482 151.974 19.482C150.054 19.482 148.47 20.122 147.222 21.402C145.974 22.65 145.35 24.266 145.35 26.25C145.35 28.234 145.974 29.866 147.222 31.146Z"
      />
      <path fill="currentColor"
        d="M175.978 21.066C175.978 21.706 176.394 22.234 177.226 22.65C178.09 23.034 179.13 23.386 180.346 23.706C181.562 23.994 182.778 24.378 183.994 24.858C185.21 25.306 186.234 26.074 187.066 27.162C187.93 28.25 188.362 29.61 188.362 31.242C188.362 33.706 187.434 35.61 185.578 36.954C183.754 38.266 181.466 38.922 178.714 38.922C173.786 38.922 170.426 37.018 168.634 33.21L174.01 30.186C174.714 32.266 176.282 33.306 178.714 33.306C180.922 33.306 182.026 32.618 182.026 31.242C182.026 30.602 181.594 30.09 180.73 29.706C179.898 29.29 178.874 28.922 177.658 28.602C176.442 28.282 175.226 27.882 174.01 27.402C172.794 26.922 171.754 26.17 170.89 25.146C170.058 24.09 169.642 22.778 169.642 21.21C169.642 18.842 170.506 16.986 172.234 15.642C173.994 14.266 176.17 13.578 178.762 13.578C180.714 13.578 182.49 14.026 184.09 14.922C185.69 15.786 186.954 17.034 187.882 18.666L182.602 21.546C181.834 19.914 180.554 19.098 178.762 19.098C177.962 19.098 177.29 19.274 176.746 19.626C176.234 19.978 175.978 20.458 175.978 21.066Z"
      />
      <path fill="currentColor"
        d="M205.369 20.202H199.945V30.186C199.945 31.018 200.153 31.626 200.569 32.01C200.985 32.394 201.593 32.618 202.393 32.682C203.193 32.714 204.185 32.698 205.369 32.634V38.25C201.113 38.73 198.105 38.33 196.345 37.05C194.617 35.77 193.753 33.482 193.753 30.186V20.202H189.577V14.25H193.753V9.402L199.945 7.53V14.25H205.369V20.202Z"
      />
      <path fill="currentColor"
        d="M229.432 35.274C226.968 37.706 223.96 38.922 220.408 38.922C216.856 38.922 213.848 37.706 211.384 35.274C208.952 32.81 207.736 29.802 207.736 26.25C207.736 22.698 208.952 19.706 211.384 17.274C213.848 14.81 216.856 13.578 220.408 13.578C223.96 13.578 226.968 14.81 229.432 17.274C231.896 19.706 233.128 22.698 233.128 26.25C233.128 29.802 231.896 32.81 229.432 35.274ZM215.752 31.002C217 32.25 218.552 32.874 220.408 32.874C222.264 32.874 223.816 32.25 225.064 31.002C226.312 29.754 226.936 28.17 226.936 26.25C226.936 24.33 226.312 22.746 225.064 21.498C223.816 20.25 222.264 19.626 220.408 19.626C218.552 19.626 217 20.25 215.752 21.498C214.536 22.746 213.928 24.33 213.928 26.25C213.928 28.17 214.536 29.754 215.752 31.002Z"
      />
      <path fill="currentColor"
        d="M243.676 18.378C244.252 16.842 245.196 15.69 246.508 14.922C247.852 14.154 249.34 13.77 250.972 13.77V20.682C249.084 20.458 247.388 20.842 245.884 21.834C244.412 22.826 243.676 24.474 243.676 26.778V38.25H237.484V14.25H243.676V18.378Z"
      />
      <path fill="currentColor"
        d="M270.757 14.25H277.381L268.645 38.25C267.397 41.706 265.733 44.202 263.653 45.738C261.605 47.306 259.045 48.01 255.973 47.85V42.09C257.637 42.122 258.949 41.77 259.909 41.034C260.901 40.298 261.685 39.114 262.261 37.482L252.421 14.25H259.189L265.429 30.378L270.757 14.25Z"
      />
      <path fill="currentColor"
        fill-rule="evenodd"
        clip-rule="evenodd"
        d="M0 15C0 11.6863 2.68629 9 6 9H27C28.6569 9 30 10.3431 30 12C30 13.6569 28.6569 15 27 15H6V34.5H27C28.6569 34.5 30 35.8431 30 37.5C30 39.1569 28.6569 40.5 27 40.5H6C2.68629 40.5 0 37.8137 0 34.5V15Z"
      />
      <path fill="currentColor"
        d="M33 6H39V43.5H33V6Z"
      />
      <path fill="currentColor"
        d="M27 3C27 1.34315 28.3431 0 30 0H42C43.6569 0 45 1.34315 45 3C45 4.65685 43.6569 6 42 6H30C28.3431 6 27 4.65685 27 3Z"
      />
      <path fill="currentColor"
        d="M27 46.5C27 44.8431 28.3431 43.5 30 43.5H42C43.6569 43.5 45 44.8431 45 46.5C45 48.1569 43.6569 49.5 42 49.5H30C28.3431 49.5 27 48.1569 27 46.5Z"
      />
    </svg>
  </span>
</template>

<style scoped lang="scss">
.ms-logo {
  display: inline-flex;
  flex: 0 0 auto;
  align-items: center;
  color: inherit;

  &--sm {
    --ms-logo-height: var(--logo-size-sm);
  }

  &--default {
    --ms-logo-height: var(--logo-size-default);
  }

  &--lg {
    --ms-logo-height: var(--logo-size-lg);
  }
}

.ms-logo__art {
  display: block;
  inline-size: auto;
  block-size: var(--ms-logo-height);
}
</style>
